/***
 *
 *   BUTTON
 *   Can be a standard button, icon button or with loading animation.
 *
 *   Styling matches ui-reference/src/components/ui/button.tsx. That component
 *   takes `variant` + `size`; this one keeps the Gravity prop surface the app's
 *   call sites already use and maps it onto the same classes. New code should
 *   prefer `variant` / `size` directly.
 *
 *   PROPS
 *   text: button label
 *   action: callback function executed on click
 *   params: object passed to the callback function (optional)
 *   variant: primary/secondary/ghost/danger/accent/warning (preferred)
 *   size: sm/md/lg (preferred)
 *   color: LEGACY — green|blue (primary), red (danger), orange (warning)
 *   outline: LEGACY — renders the secondary variant
 *   textOnly: LEGACY — renders the ghost variant
 *   small / big: LEGACY — size sm / lg
 *   icon: icon image (optional)
 *   iconPack: icon pack to use
 *   iconSize: icon size
 *   iconButton: square icon-only button
 *   alignLeft / alignRight: which side of the label the icon sits on
 *   rounded: pill corners
 *   className: pass a custom class object
 *   fullWidth: extend to full width of parent container
 *   isLoading: boolean to toggle loading animation (optional)
 *   count: number to display in a trailing count pip (optional)
 *
 **********/

import { Fragment } from 'react';
import { Icon, ClassHelper, useNavigate, cn } from 'components/lib';

import ButtonStyle from './button.tailwind.js';
import IconStyle from './icon.tailwind.js';

// LEGACY colour keys -> reference variants. `green` was the historical default.
const COLOR_VARIANT = {
    green: 'primary',
    blue: 'primary',
    red: 'danger',
    orange: 'warning',
    accent: 'accent',
};

function resolveVariant(props) {
    if (props.variant) return props.variant;
    if (props.textOnly) return 'ghost';
    if (props.outline) return 'secondary';
    if (props.color && COLOR_VARIANT[props.color]) return COLOR_VARIANT[props.color];
    // `dark` / `light` are icon colours, not button colours — those call sites
    // are bare icon buttons and want no fill.
    if (props.icon && !props.text) return 'ghost';
    return 'primary';
}

function resolveSize(props) {
    if (props.size && ButtonStyle[props.size]) return props.size;
    const small = props.small || props.size === 'sm';
    if (props.icon && !props.text) return small ? 'iconOnlySm' : 'iconOnly';
    if (small) return 'sm';
    if (props.big) return 'lg';
    return 'md';
}

export function Button(props) {
    const navigate = useNavigate();

    const variant = resolveVariant(props);
    const size = resolveSize(props);

    const buttonStyle = ClassHelper(ButtonStyle, {
        [variant]: true,
        [size]: true,
        [props.position || 'relative']: true,
        fullWidth: props.fullWidth,
        rounded: props.rounded,
        className: props.className,
    });

    const iconStyle = ClassHelper(IconStyle, {
        fill: props.fillIcon,
        alignLeft: props.alignLeft,
        alignRight: props.alignRight,
    });

    // Icon size follows the button size unless overridden — the reference uses
    // 16px glyphs in sm buttons and 18px elsewhere.
    const iconSize = props.iconSize || (size === 'sm' || size === 'iconOnlySm' ? 16 : 18);

    /* Count pip. This used to be an `absolute -left-1 -top-1` red blob: it
       clipped against the button's own leading icon, `px-1` alone made a single
       digit read as a lopsided oval, and it overflowed whatever the button sat
       in. It rides in the flex flow after the label instead — `gap-2` spaces it,
       `min-w` matched to its height keeps one digit circular, and it stays in
       the accessible name ("Filters 2") rather than being hidden.

       Tint is Badge's `count` variant, not error red: this is "2 filters are
       on", not a fault, and on the tickets toolbar it sits right beside
       CategoryMultiSelect's own count badge — two different colours for the
       same idea read as two different things. */
    const isSmall = size === 'sm' || size === 'iconOnlySm';
    const countPip =
        props.count > 0 ? (
            <span
                className={cn(
                    'inline-flex shrink-0 items-center justify-center rounded-full',
                    'bg-primary-100 font-bold leading-none text-primary-700',
                    'dark:bg-primary-900/50 dark:text-primary-200',
                    isSmall
                        ? 'h-4 min-w-[1rem] px-1 text-[10px]'
                        : 'h-[1.125rem] min-w-[1.125rem] px-1 text-[11px]'
                )}>
                {props.count > 99 ? '99+' : props.count}
            </span>
        ) : null;

    return (
        <button
            type={props.type || 'button'}
            title={props.title}
            aria-label={props.ariaLabel || (props.icon && !props.text ? props.title : undefined)}
            disabled={props.disabled || props.isLoading}
            className={buttonStyle}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                props.action && props.action(props.params);
                props.goto && navigate(props.goto);
                if (props.url) window.location = props.url;
            }}>
            {props.isLoading ? (
                <Fragment>
                    {/* Matches the reference's spinner: a ring with one
                        transparent quarter, sized to the button's text. */}
                    <span
                        className={cn(
                            // `inline-block`: an inline span ignores h-/w-.
                            'inline-block animate-loading rounded-full border-2 border-current border-t-transparent',
                            size === 'sm' || size === 'iconOnlySm' ? 'h-3.5 w-3.5' : 'h-4 w-4'
                        )}
                        aria-hidden
                    />
                    {props.text && <span className="opacity-70">{props.text}</span>}
                </Fragment>
            ) : props.icon ? (
                <Fragment>
                    <Icon
                        image={props.icon}
                        pack={props.iconPack}
                        color={props.iconColor || 'currentColor'}
                        size={iconSize}
                        className={props.iconClassName || iconStyle}
                    />
                    {props.text && props.text}
                </Fragment>
            ) : (
                props.text
            )}

            {countPip}
        </button>
    );
}
